import {fromPromise, sendParent, setup} from 'xstate';

import * as analytics from 'analytics/Analytics';
import {REGISTRY_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';

import {EntityMap_entityMap_EntityMapNode_entities as EntityMapEntityNodeOrInaccessibleInfoNode} from './__generated/EntityMap';
import {ENTITY_MAP_EDGES_PER_NODE_LIMIT} from './EntityMapConstants';
import {fetchEntityMap} from './EntityMapDAO';
import {EntityMapGraph, parseEntityMapIntoEntityMapGraph} from './EntityMapUtils';

export interface EntityNodeInput {
  entityId: string;
  entity: EntityMapEntityNodeOrInaccessibleInfoNode;
  mainEntityId: string;
}

interface EntityNodeContext {
  entityId: string;
  entity: EntityMapEntityNodeOrInaccessibleInfoNode;
  cannotBeExpandedDueToEdgeLimit: boolean;
  mainEntityId: string;
}

interface EntityNodeEvents {
  type: 'loadExtendedGraph';
}

function doesEntityHaveTooManyEdges(entity: EntityMapEntityNodeOrInaccessibleInfoNode): boolean {
  return isInaccessible(entity) ? false : entity.edgeCount > ENTITY_MAP_EDGES_PER_NODE_LIMIT;
}

/**
 * Returns true if the entity can be expanded, i.e. it is accessible and has not already been fully expanded.
 */
function canEntityBeExpanded(context: EntityNodeContext): boolean {
  return !isInaccessible(context.entity) && !context.cannotBeExpandedDueToEdgeLimit;
}

/**
 * XState state machine for managing an individual entity node within an entity map graph, for a given
 * entityId, and loading relationships from that entity.
 *
 * The idle state handles the loadExtendedGraph event, which can be sent from the view using actorRef.send().
 */
export const EntityNodeMachine = setup({
  types: {
    input: {} as EntityNodeInput,
    context: {} as EntityNodeContext,
    events: {} as EntityNodeEvents,
  },
  actors: {
    loadExtendedGraph: fromPromise<EntityMapGraph, {entityId: string; mainEntityId: string}>(
      async ({input: {entityId, mainEntityId}}) => {
        const entityMap = await fetchEntityMap(entityId, 1);
        analytics.trackEvent(REGISTRY_ANALYTICS_ACTIONS.EXPANDED_ENTITY_NODE_IN_ENTITY_MAP, {
          entityId,
          mainEntityId,
        });
        return parseEntityMapIntoEntityMapGraph(entityMap);
      }
    ),
  },
  actions: {
    // Extracted to a named action to make it easier to stub in tests using EntityNodeMachine.provide()
    notifyParent: sendParent((_, params: EntityMapGraph) => ({
      type: 'completedFetchingExtendedGraph',
      ...params,
    })),
  },
}).createMachine({
  context: ({input}) => ({
    entityId: input.entityId,
    entity: input.entity,
    cannotBeExpandedDueToEdgeLimit: doesEntityHaveTooManyEdges(input.entity),
    mainEntityId: input.mainEntityId,
  }),
  initial: 'checkIfExpandable',
  states: {
    checkIfExpandable: {
      always: [
        {
          target: 'idle',
          guard: ({context}) => canEntityBeExpanded(context),
        },
        {
          target: 'done',
          guard: ({context}) => !canEntityBeExpanded(context),
        },
      ],
    },
    idle: {
      on: {
        loadExtendedGraph: {
          target: 'loading',
        },
      },
    },
    loading: {
      invoke: {
        id: 'loadExtendedGraphActorFromLoadingState',
        input: ({context}) => ({
          entityId: context.entityId,
          mainEntityId: context.mainEntityId,
        }),
        src: 'loadExtendedGraph',
        onError: {
          target: 'error',
        },
        onDone: {
          target: 'done',
          actions: [
            {
              type: 'notifyParent',
              params: ({event}) => event.output,
            },
          ],
        },
      },
    },
    error: {
      type: 'final',
    },
    done: {
      type: 'final',
    },
  },
});
